import { useCallback, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import "./App.css";
import AppNavbar from "./AppNavbar.js";
import SectionCard from "./SectionCard.js";
import Social from "./Social.js";
import {
  buildLoginUrl,
  buildLogoutUrl,
  buildSignupUrl,
  getTokenStorageKey,
  isTokenExpired,
  readTokenFromUrlHash,
} from "./auth.js";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const COGNITO_REDIRECT_URI = `${window.location.origin}/`;
const COGNITO_LOGOUT_URI = `${window.location.origin}/`;

function LeaderboardPage() {
  const [idToken, setIdToken] = useState("");
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState({ cash_available: 0, cash_reserved: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const tokenFromHash = readTokenFromUrlHash();
    const storedToken = localStorage.getItem(getTokenStorageKey()) || "";
    const token = tokenFromHash || storedToken;

    if (!token || isTokenExpired(token)) {
      localStorage.removeItem(getTokenStorageKey());
      setIdToken("");
      return;
    }

    localStorage.setItem(getTokenStorageKey(), token);
    setIdToken(token);
    if (tokenFromHash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loginUrl = useMemo(
    () => buildLoginUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const signupUrl = useMemo(
    () => buildSignupUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const logoutUrl = useMemo(
    () => buildLogoutUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_LOGOUT_URI),
    []
  );

  const apiFetch = useCallback(async (path) => {
    if (!API_BASE_URL) {
      throw new Error("Missing REACT_APP_API_BASE_URL");
    }
    if (!idToken) {
      throw new Error("Not signed in");
    }

    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    });
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body.error || body.message || "Request failed");
    }
    return body;
  }, [idToken]);

  useEffect(() => {
    let cancelled = false;

    const loadAccountHeader = async () => {
      if (!idToken) {
        setMe(null);
        setBalance({ cash_available: 0, cash_reserved: 0 });
        setError("");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const [meRes, balanceRes] = await Promise.all([
          apiFetch("/me"),
          apiFetch("/me/balance"),
        ]);
        if (!cancelled) {
          setMe(meRes);
          setBalance(balanceRes);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load account");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAccountHeader();
    return () => {
      cancelled = true;
    };
  }, [idToken, apiFetch]);

  const handleLogin = () => {
    if (!loginUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(loginUrl);
  };

  const handleSignup = () => {
    if (!signupUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(signupUrl);
  };

  const handleLogout = () => {
    localStorage.removeItem(getTokenStorageKey());
    setIdToken("");
    setMe(null);
    setBalance({ cash_available: 0, cash_reserved: 0 });
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  };

  return (
    <div className="App">
      <AppNavbar
        links={[
          { href: "/", label: "Home" },
          { href: "/open-orders", label: "Open Orders" },
          { href: "/communities", label: "Communities" },
          { href: "/leaderboard", label: "Leaderboard" },
          { href: "/market-resolution", label: "Resolve Market" },
          { href: "/markets/new", label: "Create Market" },
        ]}
        isSignedIn={Boolean(idToken)}
        me={me}
        balance={balance}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onSignup={handleSignup}
      />
      <Container className="py-4">
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" className="mb-0">{error}</Alert>
            </Col>
          </Row>
        )}
        {!idToken ? (
          <Row>
            <Col>
              <Alert variant="info" className="mb-0">
                Sign in with Cognito to view the leaderboard.
              </Alert>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col>
              <SectionCard title="Leaderboard">
                <Social idToken={idToken} disabled={loading} />
              </SectionCard>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}

export default LeaderboardPage;
